import { getFieldErrorStyles } from '@apia/components';
import React from 'react';
import { Input, InputProps } from '@apia/theme';
import { useFormContext } from '../form/FormContext';
import { useValidationClass } from '../hooks';
import { useField, TUseField } from '../hooks/useField';
import { lang } from '../util';
import { FieldWrapper, IWrapper } from './FieldWrapper';
import { TDefaultFieldType } from './types';

type TFileInput = Omit<InputProps, 'onChange' | 'type' | 'value' | 'multiple'> &
  TDefaultFieldType<File | null> &
  Pick<TUseField, 'removeOnUnmount'> &
  Pick<IWrapper, 'avoidSemicolon'>;

export const FileInput = React.memo(
  React.forwardRef<HTMLInputElement, TFileInput>(
    (
      {
        avoidSemicolon,
        className,
        label,
        /**
         * useField
         */
        avoidRegisterIfExists,
        formName: propsFormName,
        initialValue,
        name,
        removeOnUnmount,
        submitValueParser,
        validationClass,
        validationFunction,
        validationRules,
        ...props
      },
      ref,
    ) => {
      const { name: formName } = useFormContext();
      const { actualValidationFunction, actualValidationRules } =
        useValidationClass({
          formName,
          name,
          customValidation(fileValue) {
            return fileValue !== null && fileValue !== undefined
              ? true
              : lang('required');
          },
          validationClass,
          validationFunction,
          validationRules,
        });
      const {
        error,
        onChange,
        value,
        updateValidations,
        isTouched,
        ...validationProps
      } = useField<File | null>({
        formName,
        name,
        removeOnUnmount,
        submitValueParser,
        validationFunction: actualValidationFunction,
        validationRules: actualValidationRules,
      });

      return (
        <FieldWrapper
          name={name}
          label={label}
          className={`input ${className ?? ''}`}
          avoidSemicolon={avoidSemicolon}
          title={props.title}
        >
          <Input
            {...props}
            {...validationProps}
            type="file"
            aria-label={props['aria-label'] ?? label}
            ref={ref}
            sx={getFieldErrorStyles(!error)}
            onChange={React.useCallback(
              (ev: React.ChangeEvent<HTMLInputElement>) => {
                onChange(ev.target?.files?.[0] ?? null);
              },
              [onChange],
            )}
          />
        </FieldWrapper>
      );
    },
  ),
);

FileInput.displayName = 'FileInput';
